import {
    Box,
    Text,
    Flex,
    IconButton,
    Tooltip,
    useBreakpointValue,
    useToast,
    AlertDialog,
    AlertDialogOverlay,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogBody,
    AlertDialogFooter,
    Button
} from '@chakra-ui/react';
import { useDisclosure, CloseButton } from "@chakra-ui/react";
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { FiArrowDown, FiArrowUp } from 'react-icons/fi';
import { useEffect, useRef, useState } from "react";
import EventsService from "../../api/services/EventsService";

const EventParticipantsCardList = ({ eventId, isEditing }) => {
    const [sort, setSort] = useState('id');
    const [sortDir, setSortDir] = useState('desc');
    const [loading, setLoading] = useState(true);
    const [participants, setParticipants] = useState([]);
    const [selectedParticipant, setSelectedParticipant] = useState(null);

    const toast = useToast();
    const navigate = useNavigate();
    const isMobile = useBreakpointValue({ base: true, md: false });

    const { isOpen, onOpen, onClose } = useDisclosure();
    const cancelRef = useRef();

    const handleSort = (column) => {
        if (sort === column) {
            setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
        } else {
            setSort(column);
            setSortDir('asc');
        }
    };

    const fetchParticipants = async () => {
        setLoading(true);
        try {
            if (eventId) {
                const res = await EventsService.getEventParticipants(eventId);
                setParticipants(res.data.data);
            }
        } catch (error) {
            toast({ title: 'Błąd pobierania danych', status: 'error', duration: 3000, isClosable: true });
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        try {
            await EventsService.deleteEventParticipant(eventId, selectedParticipant.id);
            toast({
                title: 'Uczestnik usunięty.',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            fetchParticipants();
        } catch (error) {
            toast({
                title: 'Błąd podczas usuwania uczestnika',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            onClose();
            setSelectedParticipant(null);
        }
    };

    useEffect(() => {
        fetchParticipants();
    }, [eventId, sort, sortDir]);

    const columns = [
        { key: 'id', label: 'ID' },
        { key: 'name', label: 'Imię i nazwisko' },
        { key: 'email', label: 'Adres e-mail' },
        { key: 'date', label: 'Data dołączenia' },
        { key: 'actions', label: 'Akcje' },
    ];

    const renderSortIcon = (key) => {
        if (sort !== key) return null;
        return sortDir === 'asc' ? <FiArrowUp /> : <FiArrowDown />;
    };

    return (
        <Box w="100%" maxW="7xl" mx="auto">
            {!isMobile && (
                <Box
                    display="grid"
                    gridTemplateColumns="80px 1fr 1fr 140px 100px"
                    gap="16px"
                    mb="8px"
                >
                    {columns.map(col => (
                        <Box
                            key={col.key}
                            bg="#0A4725"
                            borderRadius="0px"
                            p="12px"
                            display="flex"
                            alignItems="center"
                            fontSize="14px"
                            fontWeight="400"
                            color="white"
                            cursor={col.key !== 'actions' ? 'pointer' : 'default'}
                            onClick={() => col.key !== 'actions' && handleSort(col.key)}
                            boxShadow="sm"
                        >
                            <Flex align="center" gap={1}>
                                {col.label}
                                {renderSortIcon(col.key)}
                            </Flex>
                        </Box>
                    ))}
                </Box>
            )}

            {participants.map(participant => (
                <Box
                    key={participant.id}
                    display="grid"
                    gridTemplateColumns={{
                        base: '1fr',
                        md: '80px 1fr 1fr 140px 100px'
                    }}
                    gap="16px"
                    bg="transparent"
                    p="0"
                    mb="12px"
                    fontFamily="Inter"
                    fontSize="14px"
                    fontWeight="400"
                >
                    <Box bg="white" borderRadius="0px" p="12px" display="flex" alignItems="center">
                        <Text>{participant.participantId}</Text>
                    </Box>

                    <Box bg="white" borderRadius="0px" p="12px" display="flex" alignItems="center">
                        <Text>{participant.fullName}</Text>
                    </Box>

                    <Box bg="white" borderRadius="0px" p="12px" display="flex" alignItems="center">
                        <Text>{participant.email}</Text>
                    </Box>

                    <Box bg="white" borderRadius="0px" p="12px" display="flex" alignItems="center">
                        <Text>{moment(participant.registeredAt).format('D.MM.YYYY H:mm')}</Text>
                    </Box>

                    <Box bg="white" borderRadius="0px" p="8px" display="flex" justifyContent="center" gap={2} alignItems="center">
                        <Tooltip label="Usuń">
                            <IconButton
                                icon={
                                    <svg width="15" height="16" viewBox="0 0 15 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path fillRule="evenodd" clipRule="evenodd" d="M6.5 0C6.31434 9.91486e-05 6.13237 0.0518831 5.97447 0.149552C5.81658 0.247222 5.68899 0.386919 5.606 0.553L4.882 2H1.5C1.23478 2 0.98043 2.10536 0.792893 2.29289C0.605357 2.48043 0.5 2.73478 0.5 3C0.5 3.26522 0.605357 3.51957 0.792893 3.70711C0.98043 3.89464 1.23478 4 1.5 4V14C1.5 14.5304 1.71071 15.0391 2.08579 15.4142C2.46086 15.7893 2.96957 16 3.5 16H11.5C12.0304 16 12.5391 15.7893 12.9142 15.4142C13.2893 15.0391 13.5 14.5304 13.5 14V4C13.7652 4 14.0196 3.89464 14.2071 3.70711C14.3946 3.51957 14.5 3.26522 14.5 3C14.5 2.73478 14.3946 2.48043 14.2071 2.29289C14.0196 2.10536 13.7652 2 13.5 2H10.118L9.394 0.553C9.31101 0.386919 9.18342 0.247222 9.02553 0.149552C8.86763 0.0518831 8.68566 9.91486e-05 8.5 0H6.5ZM4.5 6C4.5 5.73478 4.60536 5.48043 4.79289 5.29289C4.98043 5.10536 5.23478 5 5.5 5C5.76522 5 6.01957 5.10536 6.20711 5.29289C6.39464 5.48043 6.5 5.73478 6.5 6V12C6.5 12.2652 6.39464 12.5196 6.20711 12.7071C6.01957 12.8946 5.76522 13 5.5 13C5.23478 13 4.98043 12.8946 4.79289 12.7071C4.60536 12.5196 4.5 12.2652 4.5 12V6ZM9.5 5C9.23478 5 8.98043 5.10536 8.79289 5.29289C8.60536 5.48043 8.5 5.73478 8.5 6V12C8.5 12.2652 8.60536 12.5196 8.79289 12.7071C8.98043 12.8946 9.23478 13 9.5 13C9.76522 13 10.0196 12.8946 10.2071 12.7071C10.3946 12.5196 10.5 12.2652 10.5 12V6C10.5 5.73478 10.3946 5.48043 10.2071 5.29289C10.0196 5.10536 9.76522 5 9.5 5Z" fill="#659479"/>
                                    </svg>
                                }
                                aria-label="Usuń"
                                size="sm"
                                onClick={() => {
                                    setSelectedParticipant(participant);
                                    onOpen();
                                }}
                                bg="white"
                                disabled={!isEditing}
                            />
                        </Tooltip>
                    </Box>
                </Box>
            ))}

            {/* Modal potwierdzający usunięcie */}
            <AlertDialog
                isOpen={isOpen}
                leastDestructiveRef={cancelRef}
                onClose={onClose}
                isCentered
            >
                <AlertDialogOverlay bg="blackAlpha.300">
                    <AlertDialogContent borderRadius="0" maxW="660px" mx={4} textAlign="center" px={6} py={6} position="relative">
                        <CloseButton
                            position="absolute"
                            top="16px"
                            right="16px"
                            onClick={onClose}
                            color="black"
                        />
                        <AlertDialogHeader
                            fontSize="20px"
                            fontWeight="bold"
                            color="black"
                            pb={2}
                            textAlign="left"
                        >
                            Usuń uczestnika
                        </AlertDialogHeader>

                        <AlertDialogBody fontSize="16px" color="black" whiteSpace="pre-line" py="48px">
                            {selectedParticipant && (
                                <>
                                    <Text fontWeight={600} fontSize="22px">
                                        Czy na pewno chcesz usunąć tego uczestnika?
                                    </Text>

                                    <Box mt={4}>
                                        Zamierzasz usunąć uczestnika<br />
                                        {selectedParticipant.fullName} ({selectedParticipant.email})<br />
                                        z wydarzenia.<br />
                                        Ta operacja jest nieodwracalna.
                                    </Box>
                                </>
                            )}
                        </AlertDialogBody>

                        <AlertDialogFooter justifyContent="center" pt={6}>
                            <Button
                                ref={cancelRef}
                                onClick={onClose}
                                borderRadius="0"
                                border="1px solid #0A4725"
                                bg="white"
                                color="#0A4725"
                                px={8}
                                py={5}
                                fontWeight="500"
                                _hover={{ bg: "#f0f0f0" }}
                            >
                                Anuluj
                            </Button>
                            <Button
                                onClick={handleDelete}
                                borderRadius="0"
                                bg="#0A4725"
                                color="white"
                                ml={4}
                                px={8}
                                py={5}
                                fontWeight="500"
                                rightIcon={
                                    <svg width="19" height="20" viewBox="0 0 19 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M16.5 5L15.633 17.142C15.5971 17.6466 15.3713 18.1188 15.0011 18.4636C14.6309 18.8083 14.1439 19 13.638 19H5.362C4.85614 19 4.36907 18.8083 3.99889 18.4636C3.6287 18.1188 3.40292 17.6466 3.367 17.142L2.5 5M7.5 9V15M11.5 9V15M12.5 5V2C12.5 1.73478 12.3946 1.48043 12.2071 1.29289C12.0196 1.10536 11.7652 1 11.5 1H7.5C7.23478 1 6.98043 1.10536 6.79289 1.29289C6.60536 1.48043 6.5 1.73478 6.5 2V5M1.5 5H17.5" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                }
                                _hover={{ bg: "#0c5a32" }}
                            >
                                Usuń
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>

        </Box>
    );
};

export default EventParticipantsCardList;
